import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Eye,
  History,
  Image as ImageIcon,
  Link2,
  Plus,
  Save,
  Send,
  Settings as SettingsIcon,
  Tag,
  X,
} from 'lucide-react';

import RichEditor from '@/client/components/cms/RichEditor';
import MediaPickerDialog from '@/client/components/cms/MediaPickerDialog';
import CMSPageHistoryPanel from '@/client/components/cms/CMSPageHistoryPanel';
import CMSMenuLinksPanel from '@/client/components/cms/CMSMenuLinksPanel';
import '@/client/components/cms/editor.css';

import {
  useAdminTagsQuery,
  useCMSContentTypesQuery,
  useCMSDetailQuery,
  useCMSPageFieldsQuery,
  useCMSPageTagsQuery,
  useCMSVariantsQuery,
} from '@/client/api/cms/cms.query';
import {
  useAttachPageAssetMutation,
  useClearFeaturedAssetMutation,
  useCreateCMSMutation,
  useCreateTagMutation,
  usePublishCMSMutation,
  useSetCMSPageFieldsMutation,
  useSetCMSPageTagsMutation,
  useUnpublishCMSMutation,
  useUpdateCMSMutation,
} from '@/client/api/cms/cms.mutation';
import {
  CMS_COUNTRIES,
  CMS_LOCALES,
  CMSAsset,
  CMSObject,
  CMSPageField,
  CMSTag,
} from '@/client/types/cms';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import useDebounce from '@/client/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';

const AUTOSAVE_STORAGE_KEY = 'cms.editor.autosave';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    // Keep `/` — CMS slugs are full paths (e.g. `/content/verification-policy`).
    // Stripping slashes here mangles the canonical slug and breaks public
    // resolution (which matches on the exact stored path).
    .replace(/[^a-z0-9\s/-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/\/+/g, '/');

interface PageForm {
  title: string;
  slug: string;
  summary: string;
  content: string;
  ctype: string;
  locale: string;
  country_iso2: string;
  asid: number | null;
  hero_url: string | null;
}

const blankForm: PageForm = {
  title: '',
  slug: '',
  summary: '',
  content: '',
  ctype: 'page',
  locale: '',
  country_iso2: '',
  asid: null,
  hero_url: null,
};

const FieldInput = ({
  field,
  value,
  onChange,
}: {
  field: CMSPageField;
  value: unknown;
  onChange: (v: unknown) => void;
}) => {
  const common = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';
  switch (field.field_type) {
    case 'textarea':
    case 'richtext':
      return (
        <textarea
          className={`${common} min-h-[80px]`}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          className={common}
          value={value === null || value === undefined ? '' : Number(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          Enabled
        </label>
      );
    case 'select': {
      const opts = Array.isArray(field.options_json) ? field.options_json : [];
      return (
        <select className={common} value={String(value ?? '')} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {opts.map((o: any) => (
            <option key={String(o)} value={String(o)}>{String(o)}</option>
          ))}
        </select>
      );
    }
    case 'json':
    case 'multiselect':
      return (
        <textarea
          className={`${common} min-h-[80px] font-mono text-xs`}
          value={typeof value === 'string' ? value : JSON.stringify(value ?? '', null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              onChange(e.target.value);
            }
          }}
        />
      );
    default:
      return (
        <input
          type={field.field_type === 'url' ? 'url' : field.field_type === 'date' ? 'date' : field.field_type === 'datetime' ? 'datetime-local' : 'text'}
          className={common}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

const CMSPageEditorPage = () => {
  const navigate = useNavigate();
  const { cmsoid } = useParams<{ cmsoid?: string }>();
  const isNew = !cmsoid || cmsoid === 'new';
  const numericCmsoid = isNew ? null : Number(cmsoid);

  useBreadcrumbs([
    { label: 'Content', href: '#' },
    { label: 'Pages', href: '/admin/content/pages' },
    { label: isNew ? 'New page' : 'Edit page' },
  ]);

  const { data: existing, isLoading, isError, error, refetch } = useCMSDetailQuery(numericCmsoid, false);
  const { data: typesData } = useCMSContentTypesQuery();
  const { data: pageFields } = useCMSPageFieldsQuery(numericCmsoid);
  const { data: pageTags } = useCMSPageTagsQuery(numericCmsoid);

  const createMutation = useCreateCMSMutation();
  const updateMutation = useUpdateCMSMutation();
  const publishMutation = usePublishCMSMutation();
  const unpublishMutation = useUnpublishCMSMutation();
  const setFieldsMutation = useSetCMSPageFieldsMutation();
  const setTagsMutation = useSetCMSPageTagsMutation();
  const createTagMutation = useCreateTagMutation();
  const attachMutation = useAttachPageAssetMutation();
  const clearFeaturedMutation = useClearFeaturedAssetMutation();

  const [form, setForm] = useState<PageForm>(blankForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [autosaving, setAutosaving] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(() => {
    try {
      return localStorage.getItem(AUTOSAVE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showHeroPicker, setShowHeroPicker] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<number, unknown>>({});
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [tagSearch, setTagSearch] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const initialisedRef = useRef(false);
  const lastAutosavedRef = useRef<string>('');
  const savingRef = useRef(false);

  const { data: tagCatalog } = useAdminTagsQuery({ q: tagSearch || undefined, limit: 40 });

  const autosaveSnapshot = useMemo(
    () =>
      JSON.stringify({
        title: form.title,
        slug: form.slug,
        summary: form.summary,
        content: form.content,
        ctype: form.ctype,
        locale: form.locale,
        country_iso2: form.country_iso2,
        asid: form.asid,
      }),
    [form],
  );
  const debouncedAutosaveSnapshot = useDebounce(autosaveSnapshot, 2000);
  useEffect(() => {
    if (!existing || initialisedRef.current) return;
    const next: PageForm = {
      title: existing.title ?? '',
      slug: existing.slug ?? '',
      summary: existing.summary ?? '',
      content: existing.content ?? '',
      ctype: existing.ctype ?? 'page',
      locale: existing.locale ?? '',
      country_iso2: existing.country_iso2 ?? '',
      asid: existing.asid ?? null,
      hero_url: existing.hero_url ?? null,
    };
    setForm(next);
    setSlugTouched(true);
    initialisedRef.current = true;
    lastAutosavedRef.current = JSON.stringify({
      title: next.title,
      slug: next.slug,
      summary: next.summary,
      content: next.content,
      ctype: next.ctype,
      locale: next.locale,
      country_iso2: next.country_iso2,
      asid: next.asid,
    });
  }, [existing]);

  useEffect(() => {
    if (!pageFields) return;
    const next: Record<number, unknown> = {};
    pageFields.forEach((f) => {
      next[f.cfdid] = f.value;
    });
    setFieldValues(next);
  }, [pageFields]);

  useEffect(() => {
    if (pageTags) setSelectedTagIds(pageTags.map((t) => t.tagid));
    else if (existing?.tagid) setSelectedTagIds(existing.tagid);
  }, [pageTags, existing]);

  useEffect(() => {
    if (slugTouched) return;
    if (!form.title) return;
    setForm((p) => ({ ...p, slug: p.title }));
  }, [form.title, slugTouched]);

  const { data: variantData } = useCMSVariantsQuery(!isNew && form.slug ? form.slug : null);
  const variants = useMemo(() => {
    const list = variantData?.data?.cms_objects ?? [];
    return list.filter((v) => v.cmsoid !== numericCmsoid);
  }, [variantData, numericCmsoid]);

  const buildPayload = () => ({
    title: form.title.trim(),
    // slug: slugify(form.slug) || undefined,
    slug: form.slug || undefined,
    summary: form.summary.trim() || undefined,
    content: form.content,
    ctype: form.ctype,
    locale: form.locale || null,
    country_iso2: form.country_iso2 || null,
    ...(form.asid != null ? { asid: form.asid } : {}),
  });

  const validate = (): string | null => {
    if (!form.title.trim()) return 'Title is required';
    if (!form.ctype) return 'Type is required';
    return null;
  };

  const persistRelations = async (id: number) => {
    if (Object.keys(fieldValues).length > 0) {
      await setFieldsMutation.mutateAsync({
        cmsoid: id,
        values: Object.entries(fieldValues).map(([cfdid, value]) => ({
          cfdid: Number(cfdid),
          value,
        })),
      });
    }
    await setTagsMutation.mutateAsync({ cmsoid: id, tagids: selectedTagIds });
    if (form.asid != null) {
      try {
        await attachMutation.mutateAsync({ cmsoid: id, asid: form.asid, featured: true });
      } catch {
        /* attach may fail if already attached — asid on update still helps */
      }
    }
  };

  const handleSaveDraft = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    const err = validate();
    if (err) {
      if (!silent) toast.error(err);
      return false;
    }
    if (savingRef.current) return false;
    savingRef.current = true;
    try {
      if (isNew) {
        const result = await createMutation.mutateAsync(buildPayload());
        if (result?.cmsoid) {
          await persistRelations(result.cmsoid);
          if (!silent) toast.success('Draft saved');
          setSavedAt(new Date());
          lastAutosavedRef.current = autosaveSnapshot;
          navigate(`/admin/content/pages/${result.cmsoid}`, { replace: true });
          return true;
        }
        return false;
      }
      await updateMutation.mutateAsync({ cmsoid: numericCmsoid!, ...buildPayload() });
      if (!silent) await persistRelations(numericCmsoid!);
      if (!silent) toast.success('Saved');
      setSavedAt(new Date());
      lastAutosavedRef.current = autosaveSnapshot;
      return true;
    } catch (e: any) {
      if (!silent) toast.error(e?.message ?? 'Failed to save');
      else toast.error(e?.message ?? 'Autosave failed');
      return false;
    } finally {
      savingRef.current = false;
    }
  };

  const handlePublish = async () => {
    const verr = validate();
    if (verr) {
      toast.error(verr);
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      let id: number | null | undefined = numericCmsoid;
      if (isNew) {
        const created = await createMutation.mutateAsync(buildPayload());
        id = created?.cmsoid;
        if (id) {
          await persistRelations(id);
          navigate(`/admin/content/pages/${id}`, { replace: true });
        }
      } else {
        await updateMutation.mutateAsync({ cmsoid: numericCmsoid!, ...buildPayload() });
        await persistRelations(numericCmsoid!);
      }
      if (!id) throw new Error('Could not resolve page id');
      await publishMutation.mutateAsync(id);
      toast.success('Page published');
      setSavedAt(new Date());
      lastAutosavedRef.current = autosaveSnapshot;
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to publish');
    } finally {
      savingRef.current = false;
    }
  };
  const handleUnpublish = async () => {
    if (!numericCmsoid) return;
    try {
      await unpublishMutation.mutateAsync(numericCmsoid);
      toast.success('Reverted to draft');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to unpublish');
    }
  };

  const handleHeroSelect = async (asset: CMSAsset) => {
    const asid = Number(asset.asid);
    setForm((p) => ({ ...p, asid, hero_url: asset.public_url ?? null }));
    setShowHeroPicker(false);
    if (numericCmsoid) {
      try {
        await attachMutation.mutateAsync({ cmsoid: numericCmsoid, asid, featured: true });
        toast.success('Cover image set');
      } catch (e: any) {
        toast.error(e?.message ?? 'Could not attach cover');
      }
    }
  };

  const handleClearHero = async () => {
    setForm((p) => ({ ...p, asid: null, hero_url: null }));
    if (numericCmsoid) {
      try {
        await clearFeaturedMutation.mutateAsync(numericCmsoid);
      } catch {
        /* ignore */
      }
    }
  };

  const handleInlineAttach = async (asid: number) => {
    if (!numericCmsoid) return;
    try {
      await attachMutation.mutateAsync({ cmsoid: numericCmsoid, asid, featured: false });
    } catch {
      /* already attached or transient — content still has the image */
    }
  };

  const setAutosavePreference = (enabled: boolean) => {
    setAutosaveEnabled(enabled);
    try {
      localStorage.setItem(AUTOSAVE_STORAGE_KEY, enabled ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!autosaveEnabled) return;
    if (isNew || !numericCmsoid) return;
    if (!initialisedRef.current) return;
    if (debouncedAutosaveSnapshot === lastAutosavedRef.current) return;
    if (!form.title.trim()) return;
    if (savingRef.current || publishing || saving) return;

    let cancelled = false;
    (async () => {
      setAutosaving(true);
      const ok = await handleSaveDraft({ silent: true });
      if (!cancelled && ok) setAutosaving(false);
      else if (!cancelled) setAutosaving(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAutosaveSnapshot, autosaveEnabled, numericCmsoid, isNew]);

  const toggleTag = (tagid: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagid) ? prev.filter((id) => id !== tagid) : [...prev, tagid],
    );
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    try {
      const raw = await createTagMutation.mutateAsync({ name });
      const tag = (raw?.data ?? raw) as CMSTag;
      if (tag?.tagid) {
        setSelectedTagIds((prev) => [...prev, tag.tagid]);
        setNewTagName('');
        toast.success(`Tag "${name}" created`);
      }
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to create tag');
    }
  };

  const status = existing?.status ?? 'draft';
  const saving = createMutation.isPending || updateMutation.isPending;
  const publishing = publishMutation.isPending;
  const fields = pageFields ?? existing?.Fields ?? [];
  const catalogTags = tagCatalog?.tags ?? [];
  const selectedLabels = [
    ...(pageTags ?? []),
    ...catalogTags,
  ].filter((t, i, arr) => selectedTagIds.includes(t.tagid) && arr.findIndex((x) => x.tagid === t.tagid) === i);

  if (!isNew && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Skeleton className="mb-4 h-8 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isNew && isError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-lg rounded-lg border border-rose-100 bg-white p-8 text-center shadow-sm">
          <p className="text-base font-semibold text-rose-700">Couldn’t load this page</p>
          <p className="mt-2 text-sm text-gray-500">
            {(error as Error)?.message || 'The CMS detail request failed.'}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/content/pages')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back to list
            </Button>
            <Button size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-4 px-6 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 gap-1.5"
              onClick={() => navigate('/admin/content/pages')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="h-5 w-px bg-gray-200" />
            <div className="flex min-w-0 items-center gap-2">
              {status === 'published' ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" /> Published
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                  <Circle className="h-3 w-3" /> {status}
                </span>
              )}
              {autosaving ? (
                <span className="truncate text-xs text-gray-500">Saving…</span>
              ) : savedAt ? (
                <span className="truncate text-xs text-gray-500">
                  Saved {savedAt.toLocaleTimeString()}
                </span>
              ) : null}            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isNew && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => navigate(`/admin/content/pages/${numericCmsoid}/preview`)}
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowSettings(true)}>
              <SettingsIcon className="h-4 w-4" />
              Settings
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleSaveDraft()} disabled={saving || autosaving}>
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {status === 'published' ? (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={handleUnpublish}
                disabled={unpublishMutation.isPending}
              >
                <Circle className="h-4 w-4" />
                Unpublish
              </Button>
            ) : (
              <Button size="sm" className="gap-1.5" onClick={handlePublish} disabled={publishing || saving}>
                <Send className="h-4 w-4" />
                {publishing ? 'Publishing…' : 'Publish'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-10">
        {form.hero_url && (
          <div className="group relative mb-6">
            <img src={form.hero_url} alt="" className="max-h-[420px] w-full rounded-lg object-cover" />
            <button
              type="button"
              onClick={handleClearHero}
              className="absolute right-3 top-3 rounded-full bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
              aria-label="Remove hero image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {!form.hero_url && (
          <button
            type="button"
            onClick={() => setShowHeroPicker(true)}
            className="mb-6 flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-blue-700"
          >
            <ImageIcon className="h-4 w-4" />
            Add a cover image
          </button>
        )}

        <textarea
          rows={1}
          placeholder="Title"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          className="mb-3 w-full resize-none border-0 bg-transparent text-4xl font-bold leading-tight tracking-tight text-gray-900 outline-none placeholder-gray-300 focus:ring-0 md:text-5xl"
          onInput={(e) => {
            const t = e.currentTarget;
            t.style.height = 'auto';
            t.style.height = `${t.scrollHeight}px`;
          }}
        />

        <textarea
          rows={1}
          placeholder="A short summary (shown in search and social shares)"
          value={form.summary}
          onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
          className="mb-8 w-full resize-none border-0 bg-transparent text-lg text-gray-500 outline-none placeholder-gray-300 focus:ring-0"
          onInput={(e) => {
            const t = e.currentTarget;
            t.style.height = 'auto';
            t.style.height = `${t.scrollHeight}px`;
          }}
        />

        <RichEditor
          value={form.content}
          onChange={(content) => setForm((p) => ({ ...p, content }))}
          placeholder="Tell your story…"
          bare
          cmsoid={numericCmsoid}
          onAttachAsset={handleInlineAttach}
        />

        {fields.length > 0 && (
          <div className="mt-10 space-y-4 rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-gray-900">Custom fields</h3>
            {fields.map((f) => (
              <div key={f.cfdid} className="space-y-1.5">
                <Label>
                  {f.label}
                  {f.required ? <span className="text-red-500"> *</span> : null}
                </Label>
                <FieldInput
                  field={f}
                  value={fieldValues[f.cfdid]}
                  onChange={(v) => setFieldValues((prev) => ({ ...prev, [f.cfdid]: v }))}
                />
              </div>
            ))}
          </div>
        )}

        {variants.length > 0 && (
          <div className="mt-10 rounded-lg border border-blue-100 bg-blue-50 p-4">
            <p className="text-sm font-medium text-blue-900">
              Related variants for this slug ({variants.length})
            </p>
            <ul className="mt-1 space-y-0.5">
              {variants.map((v: CMSObject) => (
                <li key={v.cmsoid} className="text-xs text-blue-700">
                  <button
                    onClick={() => navigate(`/admin/content/pages/${v.cmsoid}`)}
                    className="hover:underline"
                  >
                    {v.locale ?? 'global'}
                    {v.country_iso2 ? ` · ${v.country_iso2}` : ''} — {v.status}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Sheet open={showSettings} onOpenChange={setShowSettings}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <SettingsIcon className="h-5 w-5" />
              Page settings
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            <label className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
              <span className="text-sm text-gray-800">
                Autosave drafts
                <span className="mt-0.5 block text-xs font-normal text-gray-500">
                  Saves about 2s after you stop typing (existing pages only)
                </span>
              </span>
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={autosaveEnabled}
                onChange={(e) => setAutosavePreference(e.target.checked)}
              />
            </label>

            <div className="space-y-1.5">
              <Label>Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => {
                  // Store the raw value so the caret doesn't jump: slugify() can
                  // shorten/collapse the string mid-edit, which resets the cursor
                  // to the end. Normalize on blur instead.
                  setSlugTouched(true);
                  setForm((p) => ({ ...p, slug: e.target.value }));
                }}
                onBlur={(e) => {
                  setForm((p) => ({ ...p, slug: slugify(e.target.value) }));
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Type</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.ctype}
                onChange={(e) => setForm((p) => ({ ...p, ctype: e.target.value }))}
              >
                {(typesData ?? [{ key: 'page', label: 'Page' }]).map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Locale</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.locale}
                  onChange={(e) => setForm((p) => ({ ...p, locale: e.target.value }))}
                >
                  {CMS_LOCALES.map((l) => (
                    <option key={l || 'global'} value={l}>{l || 'global'}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={form.country_iso2}
                  onChange={(e) => setForm((p) => ({ ...p, country_iso2: e.target.value }))}
                >
                  {CMS_COUNTRIES.map((c) => (
                    <option key={c || 'global'} value={c}>{c || 'global'}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Cover image</Label>
              {form.hero_url ? (
                <div className="relative">
                  <img src={form.hero_url} alt="" className="h-32 w-full rounded border object-cover" />
                  <button
                    type="button"
                    onClick={handleClearHero}
                    className="absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setShowHeroPicker(true)}>
                  <ImageIcon className="h-4 w-4" />
                  Choose from media library
                </Button>
              )}
            </div>

            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" /> Tags
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {selectedLabels.map((t) => (
                  <button
                    key={t.tagid}
                    type="button"
                    onClick={() => toggleTag(t.tagid)}
                    className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-800"
                  >
                    {t.name} ×
                  </button>
                ))}
              </div>
              <Input
                placeholder="Search tags…"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
              />
              <div className="max-h-32 space-y-1 overflow-y-auto">
                {catalogTags.map((t) => (
                  <button
                    key={t.tagid}
                    type="button"
                    onClick={() => toggleTag(t.tagid)}
                    className={`block w-full rounded px-2 py-1 text-left text-xs ${
                      selectedTagIds.includes(t.tagid) ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="New tag name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                />
                <Button type="button" size="sm" variant="outline" onClick={handleCreateTag} disabled={createTagMutation.isPending}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {!isNew && numericCmsoid != null && (
              <div className="space-y-2 border-t pt-4">
                <Label className="flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" /> Menu links
                </Label>
                <CMSMenuLinksPanel
                  cmsoid={numericCmsoid}
                  pageTitle={form.title || existing?.title}
                />
              </div>
            )}

            {!isNew && numericCmsoid != null && (
              <div className="space-y-2 border-t pt-4">
                <Label className="flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5" /> History
                </Label>
                <CMSPageHistoryPanel
                  cmsoid={numericCmsoid}
                  currentSlug={form.slug || existing?.slug}
                  onRestored={() => {
                    initialisedRef.current = false;
                  }}
                />
              </div>
            )}

            {!isNew && existing && (
              <div className="space-y-1 border-t pt-4 text-xs text-gray-500">
                <div>ID: <span className="font-mono">{existing.cmsoid}</span></div>
                <div>Added: {existing.date_added ? new Date(existing.date_added).toLocaleString() : '—'}</div>
                <div>Modified: {existing.date_modified ? new Date(existing.date_modified).toLocaleString() : '—'}</div>
                {existing.date_published && (
                  <div>Published: {new Date(existing.date_published).toLocaleString()}</div>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <MediaPickerDialog
        open={showHeroPicker}
        onClose={() => setShowHeroPicker(false)}
        onSelect={handleHeroSelect}
      />
    </div>
  );
};

export default CMSPageEditorPage;
